import { sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents, CartAddEvent } from '@theme/events';

/** @type {Map<string, {quantity: number, key: string}>} */
window.cartItems = new Map();
/** @type {(btn: HTMLElement, qty: number) => void} */
window.updateRemoveIcon = () => {};
/** @type {Set<string>} */
window.activeInteractions = new Set();

document.addEventListener('DOMContentLoaded', async function () {
  /** @param {HTMLElement} removeBtn @param {number} quantity */
  function updateRemoveIcon(removeBtn, quantity) {
    if (!removeBtn) return;
    const svg = removeBtn.querySelector('svg');
    if (!svg) return;
    if (quantity > 1) {
      svg.innerHTML = '<path d="M3 8H13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>';
    } else {
      svg.innerHTML = '<path d="M2 3H14M12.6667 3V13C12.6667 13.5304 12.4559 14.0391 12.0809 14.4142C11.7058 14.7893 11.1971 15 10.6667 15H5.33333C4.8029 15 4.29419 14.7893 3.91912 14.4142C3.54405 14.0391 3.33333 13.5304 3.33333 13V3M5.33333 3V2C5.33333 1.73478 5.43869 1.48043 5.62623 1.29289C5.81376 1.10536 6.06812 1 6.33333 1H9.66667C9.93188 1 10.1862 1.10536 10.3738 1.29289C10.5613 1.48043 10.6667 1.73478 10.6667 2V3M6.33333 7V11M9.66667 7V11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>';
    }
  }
  window.updateRemoveIcon = updateRemoveIcon;

  const forms = document.querySelectorAll('.featured-products__add-to-cart-form');
  await loadCartState();
  updateAllProductCards();

  forms.forEach((form) => {
    if (!(form instanceof HTMLFormElement)) return;
    const variantId = form.dataset.variantId;
    if (!variantId) return;
    const maxInventory = parseInt(form.dataset.inventoryQuantity) || 9999;
    
    const button = form.querySelector('.featured-products__add-to-cart-btn');
    const qtyControls = form.parentElement?.querySelector('.featured-products__quantity-controls');
    const qtyDisplay = qtyControls?.querySelector('.featured-products__qty-display');
    const qtyRemove = qtyControls?.querySelector('.featured-products__qty-remove');
    const qtyPlus = qtyControls?.querySelector('.featured-products__qty-plus');
    /** @type {ReturnType<typeof setTimeout> | null} */
    let pendingApiCall = null;
    
    form.addEventListener('submit', async function (e) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      if (!button || button.disabled) return;
      button.disabled = true;

      const cartItem = window.cartItems.get(variantId) || { quantity: 0, key: '' };
      cartItem.quantity = 1;
      window.cartItems.set(variantId, cartItem);
      form.style.display = 'none';
      if (qtyControls) {
        qtyControls.style.display = 'flex';
        if (qtyDisplay) qtyDisplay.textContent = '1';
        if (qtyRemove) updateRemoveIcon(qtyRemove, 1);
        if (qtyPlus && maxInventory <= 1) {
          qtyPlus.disabled = true;
        }
      }

      try {
        const formData = new FormData(form);
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData,
        });
        if (!response.ok) throw new Error('Failed to add to cart');
        const result = await response.json();
        window.cartItems.set(variantId, { quantity: 1, key: result.key });
        refreshCartDrawer();
        document.dispatchEvent(new CartAddEvent(result, 'featured-products-inline', {
          itemCount: 1,
          variantId: variantId,
          source: 'featured-products-inline',
          preventDrawerOpen: true
        }));
      } catch (error) {
        form.style.display = '';
        if (qtyControls) qtyControls.style.display = 'none';
        if (button) button.disabled = false;
        window.cartItems.delete(variantId);
      }
    });

    if (qtyPlus) {
      qtyPlus.addEventListener('click', async function () {
        const cartItem = window.cartItems.get(variantId);
        if (!cartItem) return;
        if (cartItem.quantity >= maxInventory) {
          qtyPlus.disabled = true;
          return;
        }
        window.activeInteractions.add(variantId);
        const newQty = cartItem.quantity + 1;
        if (qtyDisplay) qtyDisplay.textContent = newQty;
        if (qtyRemove) updateRemoveIcon(qtyRemove, newQty);
        cartItem.quantity = newQty;
        if (newQty >= maxInventory && qtyPlus) {
          qtyPlus.disabled = true;
        }
        if (pendingApiCall) clearTimeout(pendingApiCall);
        pendingApiCall = setTimeout(async () => {
          try {
            const currentDisplayedQty = parseInt(qtyDisplay?.textContent || newQty);
            const response = await fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
              body: JSON.stringify({ id: variantId, quantity: currentDisplayedQty }),
            });
            if (!response.ok) throw new Error('Failed to update quantity');
            const result = await response.json();
            cartItem.quantity = currentDisplayedQty;
            if (currentDisplayedQty >= maxInventory && qtyPlus) {
              qtyPlus.disabled = true;
            }
            refreshCartDrawer();
          } catch (error) {
            const revertedQty = Math.max(1, cartItem.quantity - 1);
            if (qtyDisplay) qtyDisplay.textContent = revertedQty;
            if (qtyRemove) updateRemoveIcon(qtyRemove, revertedQty);
            cartItem.quantity = revertedQty;
            if (qtyPlus) qtyPlus.disabled = false;
          }
          setTimeout(() => { window.activeInteractions.delete(variantId); }, 1000);
          pendingApiCall = null;
        }, 500);
      });
    }

    if (qtyRemove) {
      qtyRemove.addEventListener('click', async function () {
        const cartItem = window.cartItems.get(variantId);
        if (!cartItem) return;
        const currentQty = cartItem.quantity;
        const newQty = currentQty - 1;

        if (newQty <= 0) {
          if (qtyDisplay) qtyDisplay.textContent = '0';
          try {
            const response = await fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
              body: JSON.stringify({ id: variantId, quantity: 0 }),
            });
            if (!response.ok) throw new Error('Failed to remove from cart');
            window.cartItems.delete(variantId);
            if (qtyControls) qtyControls.style.display = 'none';
            form.style.display = '';
            button.disabled = false;
            if (qtyPlus) qtyPlus.disabled = false;
            refreshCartDrawer();
          } catch (error) {
            if (qtyDisplay) qtyDisplay.textContent = currentQty;
          }
        } else {
          if (qtyDisplay) qtyDisplay.textContent = newQty;
          updateRemoveIcon(qtyRemove, newQty);
          cartItem.quantity = newQty;
          if (newQty < maxInventory && qtyPlus) {
            qtyPlus.disabled = false;
          }
          if (pendingApiCall) clearTimeout(pendingApiCall);
          pendingApiCall = setTimeout(async () => {
            try {
              const currentDisplayedQty = parseInt(qtyDisplay?.textContent || newQty);
              const response = await fetch('/cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
                body: JSON.stringify({ id: variantId, quantity: currentDisplayedQty }),
              });
              if (!response.ok) throw new Error('Failed to update quantity');
              cartItem.quantity = currentDisplayedQty;
              if (currentDisplayedQty < maxInventory && qtyPlus) {
                qtyPlus.disabled = false;
              }
              refreshCartDrawer();
            } catch (error) {
              const revertedQty = currentQty;
              if (qtyDisplay) qtyDisplay.textContent = revertedQty;
              updateRemoveIcon(qtyRemove, revertedQty);
              cartItem.quantity = revertedQty;
            }
            pendingApiCall = null;
          }, 500);
        }
      });
    }
  });

  async function loadCartState() {
    try {
      const timestamp = Date.now();
      const random = Math.random();
      const response = await fetch(`/cart.js?_=${timestamp}&r=${random}`, {
        method: 'GET',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'X-Requested-With': 'XMLHttpRequest'
        },
        cache: 'no-store'
      });
      if (!response.ok) return;
      const cart = await response.json();
      window.cartItems.clear();
      cart.items.forEach((/** @type {any} */ item) => {
        window.cartItems.set(String(item.variant_id), {
          quantity: item.quantity,
          key: item.key
        });
      });
    } catch (error) {}
  }

  function refreshCartDrawer() {
    try {
      const cartDrawer = document.querySelector('cart-drawer-component');
      const cartItemsComponent = cartDrawer?.querySelector('cart-items-component');
      if (cartItemsComponent && cartItemsComponent.dataset.sectionId) {
        const sectionId = cartItemsComponent.dataset.sectionId;
        sectionRenderer.renderSection(sectionId, { cache: false }).catch((/** @type {any} */ error) => {});
      }
    } catch (error) {}
  }

  function updateAllProductCards() {
    const forms = document.querySelectorAll('.featured-products__add-to-cart-form');
    forms.forEach((form) => {
      if (!(form instanceof HTMLFormElement)) return;
      const variantId = form.dataset.variantId;
      if (!variantId) return;
      if (window.activeInteractions.has(variantId)) return;
      const maxInventory = parseInt(form.dataset.inventoryQuantity) || 9999;
      
      const button = form.querySelector('.featured-products__add-to-cart-btn');
      const qtyControls = form.parentElement?.querySelector('.featured-products__quantity-controls');
      const qtyDisplay = qtyControls?.querySelector('.featured-products__qty-display');
      const qtyRemove = qtyControls?.querySelector('.featured-products__qty-remove');
      const qtyPlus = qtyControls?.querySelector('.featured-products__qty-plus');
      const cartItem = window.cartItems?.get(variantId);
      
      if (cartItem && cartItem.quantity > 0) {
        form.style.display = 'none';
        if (qtyControls) {
          qtyControls.style.display = 'flex';
          if (qtyDisplay) qtyDisplay.textContent = cartItem.quantity;
          if (qtyRemove) updateRemoveIcon(qtyRemove, cartItem.quantity);
          if (qtyPlus) {
            qtyPlus.disabled = cartItem.quantity >= maxInventory;
          }
        }
      } else {
        form.style.display = '';
        if (qtyControls) qtyControls.style.display = 'none';
        button.disabled = false;
        if (qtyPlus) qtyPlus.disabled = false;
      }
    });
  }

  document.addEventListener(ThemeEvents.cartUpdate, async function(event) {
    await new Promise(resolve => setTimeout(resolve, 300));
    await loadCartState();
    updateAllProductCards();
  });

  document.querySelectorAll('.featured-products__hover-content').forEach(hoverContent => {
    hoverContent.addEventListener('click', function(e) {
      const target = /** @type {HTMLElement} */ (e.target);
      if (target && target.closest('button, form')) return;
      const card = /** @type {HTMLElement} */ (this).closest('.featured-products__card');
      const cardLink = /** @type {HTMLAnchorElement | null} */ (card?.querySelector('.featured-products__card-link'));
      if (cardLink && cardLink.href) window.location.href = cardLink.href;
    });
  });

  // Defensive: Remove hover content for unavailable/bundle-unavailable cards (handles dynamically injected cards)
  function enforceUnavailableHoverHidden() {
    document.querySelectorAll('.featured-products__card').forEach((card) => {
      const isUnavailable = card.classList.contains('featured-products__card--sold-out') || card.getAttribute('data-bundle-available') === 'false';
      if (isUnavailable) {
        const hover = card.querySelector('.featured-products__card-hover');
        if (hover) {
          hover.remove();
        }
      }
    });
  }

  // Run once on load
  enforceUnavailableHoverHidden();

  // Re-run after cart updates (section re-render might add cards)
  document.addEventListener(ThemeEvents.cartUpdate, () => {
    setTimeout(enforceUnavailableHoverHidden, 300);
  });

  // Observe DOM changes to catch injected cards (filters, pagination, etc.)
  const gridContainer = document.querySelector('.product-grid-container') || document.body;
  try {
    const mo = new MutationObserver(() => enforceUnavailableHoverHidden());
    mo.observe(gridContainer, { childList: true, subtree: true });
  } catch (e) {}
});
